import React, {useMemo, useState} from 'react';

import {Button, Select} from '@mantine/core';
import {Avatar} from 'src/design/atoms/Avatar';
import {Box} from 'src/design/atoms/Box';
import {Divider} from 'src/design/atoms/Divider';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import type {CourseSessionTrackerResponse} from 'src/api/ProactApiClient';
import {CourseDefinitionContentTypeEnum} from 'src/api/ProactApiClient';
import {Description} from 'src/design/atoms/Description';
import {Subtitle} from 'src/design/atoms/Typography';
import {
  filterContentsByLanguage,
  getLanguageDisplayName,
} from 'src/app/training/languageUtils';
import {CourseSummaryCard} from './CourseSummaryCard';

export interface TrackerNewSessionCardProps {
  tracker: CourseSessionTrackerResponse;
  onStartNewSession: (language?: string) => void;
}
export const TrackerNewSessionCard: React.FC<TrackerNewSessionCardProps> = ({
  tracker,
  onStartNewSession,
}) => {
  const courseDef = tracker.courseDefinition;
  const availableLanguages = courseDef.availableLanguages ?? [];
  const isMultiLanguage = availableLanguages.length > 1;

  const [selectedLanguage, setSelectedLanguage] = useState<string>(
    courseDef.defaultLanguage ?? 'en'
  );

  const filteredContents = useMemo(() => {
    const contents = courseDef.contents ?? [];
    return isMultiLanguage
      ? filterContentsByLanguage(contents, selectedLanguage)
      : contents;
  }, [courseDef.contents, selectedLanguage, isMultiLanguage]);

  const handleBegin = () => {
    onStartNewSession(isMultiLanguage ? selectedLanguage : undefined);
  };

  return (
    <Box>
      <Subtitle text={'Ready to get started?'} />
      <CourseSummaryCard course={tracker.courseDefinition} />

      {isMultiLanguage && (
        <Box mb="md" maw={300}>
          <Select
            label="Select Language"
            data={availableLanguages.map(code => ({
              value: code,
              label: getLanguageDisplayName(code),
            }))}
            value={selectedLanguage}
            onChange={val => val && setSelectedLanguage(val)}
            allowDeselect={false}
          />
        </Box>
      )}

      <p>This course contains the following content:</p>
      <Stack gap="sm" maw={360}>
        {filteredContents.map(content => {
          return (
            <Group
              key={content.id ?? content.invariantId}
              gap="sm"
              wrap="nowrap">
              <Avatar radius="xl">
                {CourseDefinitionContentTypeEnum[content.type][0]}
              </Avatar>
              <Box>
                <Text size="sm">
                  {content.lesson?.headerText ??
                    content.quiz?.headerText ??
                    content.demonstration?.headerText}
                </Text>
                <Description
                  mt={0}
                  text={CourseDefinitionContentTypeEnum[content.type]}
                />
              </Box>
            </Group>
          );
        })}
      </Stack>
      <Divider mt="md" mb="lg" w={300} />
      <Button onClick={handleBegin}>Begin</Button>
    </Box>
  );
};
